/* eslint-disable no-console */
import { useEffect, useRef, useState } from 'react';

import SoftReloadError from '~/common/errors/SoftReloadError.ts';
import { isChunkLoadingError } from '~/common/utils/error.util.tsx';

const RETRY_STORAGE_KEY = '__soft_reload_retry_state__';
const INITIAL_RETRY_STATE = { count: 0, timestamp: 0 };

/**
 * 무중단 배포 후 loading chunk 실패 이슈 우회
 * @param debounceDuration  새로고침까지 대기 시간 (milliseconds)
 * @param retryLimit 최대 재시도 횟수
 * @param retryLimitLifetime 재시도 횟수 유효 시간 (milliseconds)
 */
function useSoftReloadOnChunkError(
  debounceDuration = 1000,
  retryLimit = 3,
  retryLimitLifetime = 10 * 60 * 1000,
) {
  const [hasRetryLimitExceeded, setHasRetryLimitExceeded] = useState<boolean>(false);
  const reloadTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const loadRetryState = (): { count: number; timestamp: number } => {
    try {
      const raw = localStorage.getItem(RETRY_STORAGE_KEY);
      if (!raw) return { ...INITIAL_RETRY_STATE };

      const { count, timestamp } = JSON.parse(raw);
      if (typeof count !== 'number' || typeof timestamp !== 'number') {
        throw new Error('Invalid format');
      }

      return { count, timestamp };
    } catch {
      return { ...INITIAL_RETRY_STATE };
    }
  };

  const saveRetryState = (count: number, timestamp: number) => {
    localStorage.setItem(RETRY_STORAGE_KEY, JSON.stringify({ count, timestamp }));
  };

  const resetRetryState = (): number => {
    const { count, timestamp } = INITIAL_RETRY_STATE;
    saveRetryState(count, timestamp);
    return count;
  };

  const incrementRetryCount = (): number => {
    const { count, timestamp } = loadRetryState();
    const now = Date.now();

    const isExpired = now - timestamp > retryLimitLifetime;
    if (isExpired) {
      return resetRetryState();
    }

    const newCount = count + 1;
    saveRetryState(newCount, timestamp);
    return newCount;
  };

  const reloadSafely = () => {
    if (reloadTimeoutRef.current) return;

    reloadTimeoutRef.current = setTimeout(() => {
      const retryCount = incrementRetryCount();

      if (retryCount > retryLimit) {
        console.error(
          new SoftReloadError(
            `[soft-reload] Retry limit (${retryLimit}) exceeded. Reload skipped.`,
          ),
        );
        setHasRetryLimitExceeded(true);
        return;
      }

      console.warn('[soft-reload] Reloading due to chunk load failure');
      window.location.reload();
    }, debounceDuration);
  };

  const handlePreloadError = (event: Event) => {
    console.warn('[soft-reload] Vite preloadError detected:', event);
    reloadSafely();
  };

  const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
    if (isChunkLoadingError(event.reason)) {
      console.warn('[soft-reload] Unhandled rejection due to chunk error:', event.reason);
      reloadSafely();
    }
  };

  const handleScriptError = (event: ErrorEvent) => {
    const { target } = event;
    if (
      target instanceof HTMLScriptElement &&
      /\.(js|mjs)(\?.*)?$/.test(target.src) &&
      target.src.includes(window.location.origin)
    ) {
      console.warn('[soft-reload] Failed to load script chunk:', target.src);
      reloadSafely();
    }
  };

  useEffect(() => {
    window.addEventListener('vite:preloadError', handlePreloadError);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);
    window.addEventListener('error', handleScriptError, true);

    return () => {
      window.removeEventListener('vite:preloadError', handlePreloadError);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
      window.removeEventListener('error', handleScriptError, true);

      if (reloadTimeoutRef.current) {
        clearTimeout(reloadTimeoutRef.current);
        reloadTimeoutRef.current = null;
      }
    };
  }, [debounceDuration, retryLimit, retryLimitLifetime]);

  return { hasRetryLimitExceeded };
}

export default useSoftReloadOnChunkError;
